import React, { useMemo, useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Plus, Trash2, Copy, Save, X, Calendar as CalendarIcon, Boxes, Package } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";

import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { MultiSelect } from "@/components/MultiSelect";
import { useStatusOptions } from "@/hooks/useStatusOptions";
import { useEvidenciaStatusOptions } from "@/hooks/useEvidenciaStatusOptions";
import { useEsiStatusOptions } from "@/hooks/useEsiStatusOptions";
import { cn } from "@/lib/utils";

const emptyItem = () => ({
  po: "", sku: "", vpn: "", upc: "", pedimento: "", pz: 0,
  faltante: 0, sobrante: 0, comentarios: "",
  clasificacion_b: 0, clasificacion_c: 0, clasificacion_d: 0,
});

function DatePicker({ value, onChange, testId, placeholder }) {
  const date = value ? new Date(value) : undefined;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          type="button"
          data-testid={testId}
          className={cn("w-full justify-start h-10 font-normal", !value && "text-muted-foreground")}
        >
          <CalendarIcon className="h-4 w-4 mr-2" />
          {value ? value : placeholder || "Seleccionar fecha"}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={date}
          onSelect={(d) => onChange(d ? format(d, "yyyy-MM-dd") : "")}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}

export default function OTEditPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { options: STATUS_OPTIONS } = useStatusOptions();
  const { options: EVIDENCIA_STATUS_OPTIONS } = useEvidenciaStatusOptions();
  const { options: ESI_STATUS_OPTIONS } = useEsiStatusOptions();

  const { data: catalogs = [] } = useQuery({
    queryKey: ["catalogs-all"],
    queryFn: async () => (await api.get("/catalogs")).data,
  });

  const { data: ot, isLoading: loadingOT } = useQuery({
    queryKey: ["ot", id],
    queryFn: async () => (await api.get(`/ots/${id}`)).data,
  });

  const opts = useMemo(() => {
    const by = (t) => catalogs.filter((c) => c.type === t).map((c) => c.name);
    return {
      clientes: by("cliente"),
      solicitantes: by("solicitante"),
      tipos: by("tipo_trabajo"),
      puertos: by("puerto_entrada"),
      centros: by("centro"),
    };
  }, [catalogs]);

  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!ot) return;
    setForm({
      cliente: ot.cliente || "",
      solicitante: ot.solicitante || "",
      fecha_arribo: ot.fecha_arribo || "",
      fecha_entrega: ot.fecha_entrega || "",
      centro: ot.centro || "",
      puerto_entrada: ot.puerto_entrada || "",
      tipos_trabajo: ot.tipos_trabajo || [],
      observaciones: ot.observaciones || "",
      estatus: ot.estatus || "Pendiente",
      estatus_evidencia: ot.estatus_evidencia || "Pendiente de subir",
      estatus_esi: ot.estatus_esi || "Pendiente revisión",
      items: (ot.items && ot.items.length > 0) ? ot.items.map((it) => ({
        po: it.po || "",
        sku: it.sku || "",
        vpn: it.vpn || "",
        upc: it.upc || "",
        pedimento: it.pedimento || "",
        pz: parseInt(it.pz) || 0,
        faltante: parseInt(it.faltante) || 0,
        sobrante: parseInt(it.sobrante) || 0,
        comentarios: it.comentarios || "",
        clasificacion_b: parseInt(it.clasificacion_b) || 0,
        clasificacion_c: parseInt(it.clasificacion_c) || 0,
        clasificacion_d: parseInt(it.clasificacion_d) || 0,
      })) : [emptyItem()],
    });
  }, [ot]);

  if (loadingOT || !form) {
    return <div className="text-sm text-muted-foreground">Cargando OT...</div>;
  }

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const NUMERIC_KEYS = new Set(["pz", "faltante", "sobrante", "clasificacion_b", "clasificacion_c", "clasificacion_d"]);
  const updateItem = (idx, k, v) => {
    const items = [...form.items];
    items[idx] = { ...items[idx], [k]: NUMERIC_KEYS.has(k) ? (parseInt(v) || 0) : v };
    setForm({ ...form, items });
  };
  const addRow = () => setForm({ ...form, items: [...form.items, emptyItem()] });
  const dupRow = (idx) => setForm({ ...form, items: [...form.items.slice(0, idx + 1), { ...form.items[idx] }, ...form.items.slice(idx + 1)] });
  const delRow = (idx) => setForm({ ...form, items: form.items.filter((_, i) => i !== idx) });

  const totalPz = form.items.reduce((a, b) => a + (parseInt(b.pz) || 0), 0);
  const totalFaltante = form.items.reduce((a, b) => a + (parseInt(b.faltante) || 0), 0);
  const totalSobrante = form.items.reduce((a, b) => a + (parseInt(b.sobrante) || 0), 0);
  const uniqueSkus = new Set(form.items.map((i) => i.sku).filter(Boolean)).size;
  const uniqueVpns = new Set(form.items.map((i) => i.vpn).filter(Boolean)).size;

  const rowErrors = form.items.map((it) => {
    const pz = parseInt(it.pz) || 0;
    const b = parseInt(it.clasificacion_b) || 0;
    const c = parseInt(it.clasificacion_c) || 0;
    const d = parseInt(it.clasificacion_d) || 0;
    const sum = b + c + d;
    return sum > pz ? `Piezas clasificadas (${sum}) exceden PZ (${pz})` : "";
  });
  const hasClasErrors = rowErrors.some((e) => e);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.cliente) return toast.error("Selecciona un cliente");
    if (!form.solicitante) return toast.error("Selecciona un solicitante");
    if (hasClasErrors) {
      const badIdx = rowErrors.findIndex((e) => e);
      return toast.error(
        `Revisa la partida ${badIdx + 1}: ${rowErrors[badIdx]}. Ajusta las piezas por clasificación antes de guardar.`
      );
    }
    setSaving(true);
    try {
      await api.patch(`/ots/${id}`, form);
      toast.success(`OT ${ot.folio} actualizada`);
      navigate(`/ots/${id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al actualizar OT");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="ot-edit-form">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Editar OT <span className="font-mono text-primary">{ot.folio}</span></h1>
          <p className="text-sm text-muted-foreground">Modifica cualquier campo y guarda los cambios.</p>
        </div>
        <div className="flex gap-2">
          <Button type="button" variant="ghost" onClick={() => navigate(`/ots/${id}`)}><X className="h-4 w-4 mr-1" />Cancelar</Button>
          <Button type="submit" disabled={saving} data-testid="ot-edit-submit-button">
            <Save className="h-4 w-4 mr-1" /> {saving ? "Guardando..." : "Guardar cambios"}
          </Button>
        </div>
      </div>

      <Card className="bg-card border-border">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold tracking-wide uppercase text-muted-foreground">Datos generales</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Cliente *</Label>
            <Select value={form.cliente} onValueChange={(v) => update("cliente", v)}>
              <SelectTrigger data-testid="ot-edit-cliente-select"><SelectValue placeholder="Selecciona un cliente" /></SelectTrigger>
              <SelectContent>{opts.clientes.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Solicitante *</Label>
            <Select value={form.solicitante} onValueChange={(v) => update("solicitante", v)}>
              <SelectTrigger data-testid="ot-edit-solicitante-select"><SelectValue placeholder="Selecciona un solicitante" /></SelectTrigger>
              <SelectContent>{opts.solicitantes.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Fecha de Arribo</Label>
            <DatePicker value={form.fecha_arribo} onChange={(v) => update("fecha_arribo", v)} testId="ot-edit-fecha-arribo" placeholder="YYYY-MM-DD" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Centro</Label>
            <Select value={form.centro} onValueChange={(v) => update("centro", v)}>
              <SelectTrigger data-testid="ot-edit-centro-select"><SelectValue placeholder="Selecciona un centro" /></SelectTrigger>
              <SelectContent>{opts.centros.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Puerto de Entrada</Label>
            <Select value={form.puerto_entrada} onValueChange={(v) => update("puerto_entrada", v)}>
              <SelectTrigger data-testid="ot-edit-puerto-select"><SelectValue placeholder="Selecciona un puerto" /></SelectTrigger>
              <SelectContent>{opts.puertos.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Tipos de Trabajo</Label>
            <MultiSelect
              options={opts.tipos}
              values={form.tipos_trabajo}
              onChange={(v) => update("tipos_trabajo", v)}
              placeholder="Selecciona tipos de trabajo"
              testId="ot-edit-tipos-multiselect"
              label="tipos"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Estatus</Label>
            <Select value={form.estatus} onValueChange={(v) => update("estatus", v)}>
              <SelectTrigger data-testid="ot-edit-estatus-select"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Estatus Evidencia</Label>
            <Select value={form.estatus_evidencia} onValueChange={(v) => update("estatus_evidencia", v)}>
              <SelectTrigger data-testid="ot-edit-estatus-evidencia-select"><SelectValue /></SelectTrigger>
              <SelectContent>{EVIDENCIA_STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5 md:col-span-2">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Observaciones</Label>
            <Textarea data-testid="ot-edit-observaciones" rows={2} value={form.observaciones} onChange={(e) => update("observaciones", e.target.value)} />
          </div>
        </CardContent>
      </Card>

      <div className="flex flex-wrap gap-2">
        <div className="inline-flex items-center gap-2 rounded-full bg-card border border-border px-3 py-1.5 text-xs">
          <Boxes className="h-3.5 w-3.5 text-primary" /> SKUs únicos: <span className="font-mono tabular-nums font-semibold text-foreground">{uniqueSkus}</span>
        </div>
        <div className="inline-flex items-center gap-2 rounded-full bg-card border border-border px-3 py-1.5 text-xs">
          <Package className="h-3.5 w-3.5 text-[hsl(var(--accent))]" /> Total PZ: <span className="font-mono tabular-nums font-semibold text-foreground">{totalPz}</span>
        </div>
        <div className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs ${totalFaltante > 0 ? "bg-destructive/20 border border-destructive/60 text-destructive" : "bg-card border border-border text-muted-foreground"}`}>
          <span className="font-bold">{totalFaltante > 0 ? "⚠" : "○"}</span> Faltantes: <span data-testid="ot-edit-counter-faltante" className="font-mono tabular-nums font-semibold">{totalFaltante}</span>
        </div>
        <div className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs ${totalSobrante > 0 ? "bg-[hsl(38_92%_55%/0.2)] border border-[hsl(38_92%_55%/0.6)] text-[hsl(38_92%_55%)]" : "bg-card border border-border text-muted-foreground"}`}>
          <span className="font-bold">{totalSobrante > 0 ? "⚠" : "○"}</span> Sobrantes: <span data-testid="ot-edit-counter-sobrante" className="font-mono tabular-nums font-semibold">{totalSobrante}</span>
        </div>
        <div className="inline-flex items-center gap-2 rounded-full bg-card border border-border px-3 py-1.5 text-xs">
          <span className="text-muted-foreground">VPN únicos:</span> <span className="font-mono tabular-nums font-semibold text-foreground">{uniqueVpns}</span>
        </div>
        <div className="inline-flex items-center gap-2 rounded-full bg-card border border-border px-3 py-1.5 text-xs">
          <span className="text-muted-foreground">Partidas:</span> <span className="font-mono tabular-nums font-semibold text-foreground">{form.items.length}</span>
        </div>
        {hasClasErrors && (
          <div className="inline-flex items-center gap-2 rounded-full bg-destructive/20 border border-destructive/60 text-destructive px-3 py-1.5 text-xs">
            <span className="font-bold">⚠</span> Revisa clasificaciones: B+C+D excede PZ en {rowErrors.filter((e) => e).length} partida(s)
          </div>
        )}
      </div>

      <Card className="bg-card border-border">
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-sm font-semibold tracking-wide uppercase text-muted-foreground">Partidas (líneas)</CardTitle>
            <CardDescription className="text-xs">Modifica clasificaciones, piezas y demás datos por partida.</CardDescription>
          </div>
          <Button type="button" size="sm" onClick={addRow} data-testid="ot-edit-add-row">
            <Plus className="h-4 w-4 mr-1" /> Agregar fila
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 border-y border-border">
                <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground">
                  <th className="py-2 px-2 w-10">#</th>
                  <th className="py-2 px-2 min-w-[110px]">PO</th>
                  <th className="py-2 px-2 min-w-[120px]">SKU</th>
                  <th className="py-2 px-2 min-w-[110px]">VPN</th>
                  <th className="py-2 px-2 min-w-[110px]">UPC</th>
                  <th className="py-2 px-2 min-w-[110px]">Pedimento</th>
                  <th className="py-2 px-2 w-20">PZ</th>
                  <th className="py-2 px-2 w-20">Faltante</th>
                  <th className="py-2 px-2 w-20">Sobrante</th>
                  <th className="py-2 px-2 min-w-[160px]">Comentarios</th>
                  <th className="py-2 px-2 w-20">Clas. B</th>
                  <th className="py-2 px-2 w-20">Clas. C</th>
                  <th className="py-2 px-2 w-20">Clas. D</th>
                  <th className="py-2 px-2 w-20"></th>
                </tr>
              </thead>
              <tbody>
                {form.items.map((it, idx) => {
                  const rowErr = rowErrors[idx];
                  const pzInt = parseInt(it.pz) || 0;
                  const bInt = parseInt(it.clasificacion_b) || 0;
                  const cInt = parseInt(it.clasificacion_c) || 0;
                  const dInt = parseInt(it.clasificacion_d) || 0;
                  const isOver = (bInt + cInt + dInt) > pzInt;
                  return (
                  <tr
                    key={idx}
                    className={`border-b border-border/60 ${isOver ? "bg-destructive/5" : ""}`}
                    data-testid={`ot-edit-row-${idx}`}
                  >
                    <td className="py-1.5 px-2 text-muted-foreground font-mono text-xs">{idx + 1}</td>
                    {["po", "sku", "vpn", "upc", "pedimento"].map((k) => (
                      <td key={k} className="py-1.5 px-1">
                        <Input value={it[k] || ""} onChange={(e) => updateItem(idx, k, e.target.value)} className="h-9 font-mono text-xs" data-testid={`ot-edit-${k}-${idx}`} />
                      </td>
                    ))}
                    <td className="py-1.5 px-1">
                      <Input type="number" min="0" value={it.pz} onChange={(e) => updateItem(idx, "pz", e.target.value)} className={`h-9 font-mono text-xs ${isOver ? "border-destructive/60" : ""}`} data-testid={`ot-edit-pz-${idx}`} />
                    </td>
                    <td className="py-1.5 px-1">
                      <Input
                        type="number"
                        min="0"
                        value={it.faltante || 0}
                        onChange={(e) => updateItem(idx, "faltante", e.target.value)}
                        className={`h-9 font-mono text-xs ${(parseInt(it.faltante) || 0) > 0 ? "bg-destructive/15 border-destructive/60 text-destructive font-semibold" : ""}`}
                        data-testid={`ot-edit-faltante-${idx}`}
                      />
                    </td>
                    <td className="py-1.5 px-1">
                      <Input
                        type="number"
                        min="0"
                        value={it.sobrante || 0}
                        onChange={(e) => updateItem(idx, "sobrante", e.target.value)}
                        className={`h-9 font-mono text-xs ${(parseInt(it.sobrante) || 0) > 0 ? "bg-[hsl(38_92%_55%/0.15)] border-[hsl(38_92%_55%/0.6)] text-[hsl(var(--status-pending))] font-semibold" : ""}`}
                        data-testid={`ot-edit-sobrante-${idx}`}
                      />
                    </td>
                    <td className="py-1.5 px-1">
                      <Input
                        value={it.comentarios || ""}
                        onChange={(e) => updateItem(idx, "comentarios", e.target.value)}
                        placeholder={rowErr || "Observación de la partida"}
                        className={`h-9 text-xs ${(parseInt(it.faltante) || 0) > 0 ? "bg-destructive/10 border-destructive/40" : (parseInt(it.sobrante) || 0) > 0 ? "bg-[hsl(38_92%_55%/0.1)]" : ""}`}
                        data-testid={`ot-edit-comentarios-${idx}`}
                      />
                    </td>
                    {["clasificacion_b", "clasificacion_c", "clasificacion_d"].map((k) => (
                      <td key={k} className="py-1.5 px-1">
                        <Input
                          type="number"
                          min="0"
                          value={it[k] || 0}
                          onChange={(e) => updateItem(idx, k, e.target.value)}
                          className={`h-9 font-mono text-xs ${isOver ? "border-destructive/60 bg-destructive/10 text-destructive" : ""}`}
                          data-testid={`ot-edit-${k}-${idx}`}
                          title={isOver ? rowErr : ""}
                        />
                      </td>
                    ))}
                    <td className="py-1.5 px-1">
                      <div className="flex gap-0.5">
                        <Button type="button" size="icon" variant="ghost" className="h-8 w-8" onClick={() => dupRow(idx)} title="Duplicar"><Copy className="h-3.5 w-3.5" /></Button>
                        <Button type="button" size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => delRow(idx)} title="Eliminar" data-testid={`ot-edit-delete-${idx}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
              <tfoot className="sticky bottom-0 bg-card border-t-2 border-primary/40">
                <tr className="text-xs">
                  <td colSpan={6} className="py-2 px-2 text-right text-muted-foreground uppercase tracking-wider">Totales:</td>
                  <td className="py-2 px-2 font-mono font-semibold text-foreground">{totalPz}</td>
                  <td className={`py-2 px-2 font-mono font-semibold ${totalFaltante > 0 ? "text-destructive" : "text-muted-foreground"}`} data-testid="ot-edit-total-faltante">
                    {totalFaltante > 0 ? `⚠ ${totalFaltante}` : "0"}
                  </td>
                  <td className={`py-2 px-2 font-mono font-semibold ${totalSobrante > 0 ? "text-[hsl(var(--status-pending))]" : "text-muted-foreground"}`} data-testid="ot-edit-total-sobrante">
                    {totalSobrante > 0 ? `⚠ ${totalSobrante}` : "0"}
                  </td>
                  <td colSpan={4} className="py-2 px-2 text-muted-foreground">SKUs: <span className="font-mono text-foreground font-semibold">{uniqueSkus}</span></td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>
    </form>
  );
}
